import React, { useCallback, useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash, X } from "@phosphor-icons/react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function WorkforceShifts() {
  const [staffList, setStaffList] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ staff_id: "", day_of_week: 0, start_time: "09:00", end_time: "17:00" });

  const load = useCallback(async () => {
    try {
      const [s, sh] = await Promise.all([api.get("/staff"), api.get("/workforce/shifts")]);
      setStaffList(s.data);
      setShifts(sh.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/workforce/shifts", { ...form, day_of_week: Number(form.day_of_week) });
      toast.success("Shift saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this shift?")) return;
    try {
      await api.delete(`/workforce/shifts/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Build grid: staff × day
  const grid = {};
  shifts.forEach(s => {
    grid[s.staff_id] = grid[s.staff_id] || {};
    grid[s.staff_id][s.day_of_week] = s;
  });

  return (
    <AppShell title="Shift Scheduler" subtitle="Weekly Roster"
      actions={
        <button data-testid="btn-add-shift" onClick={() => setOpen(true)}
                className="inline-flex items-center gap-2 h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
          <Plus size={14} weight="bold"/>Add shift
        </button>
      }
    >
      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="shifts-grid">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-4 py-3">Staff</th>
              {DAYS.map((d) => <th key={d} className="px-3 py-3 text-center">{d}</th>)}
            </tr>
          </thead>
          <tbody>
            {staffList.map((s, i) => (
              <tr key={s.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                <td className="px-4 py-3 font-medium">{s.name}<div className="text-[10px] text-[#8A8780] font-mono">{s.staff_code || ""}</div></td>
                {DAYS.map((day, d) => {
                  const shift = grid[s.id]?.[d];
                  return (
                    <td key={day} className="px-3 py-3 text-center">
                      {shift ? (
                        <div className="inline-flex items-center gap-1">
                          <span className="text-xs font-mono bg-[#D4E0D1] text-[#1A3317] px-2 py-1 rounded">{shift.start_time}–{shift.end_time}</span>
                          <button onClick={() => del(shift.id)} className="text-[#C25D38]" data-testid={`shift-del-${shift.id}`}><Trash size={12}/></button>
                        </div>
                      ) : <span className="text-[#E5E2D9]">—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-md w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading text-lg">New shift</div>
              <button onClick={() => setOpen(false)} className="p-1"><X size={16}/></button>
            </div>
            <form onSubmit={save} className="space-y-3" data-testid="shift-form">
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1">Staff</label>
                <select required value={form.staff_id} onChange={e => setForm(f=>({...f,staff_id:e.target.value}))}
                        className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white" data-testid="shift-staff">
                  <option value="">— select —</option>
                  {staffList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1">Day</label>
                <select value={form.day_of_week} onChange={e => setForm(f=>({...f,day_of_week:e.target.value}))}
                        className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
                  {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1">Start</label>
                  <input type="time" value={form.start_time} onChange={e => setForm(f=>({...f,start_time:e.target.value}))}
                         className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1">End</label>
                  <input type="time" value={form.end_time} onChange={e => setForm(f=>({...f,end_time:e.target.value}))}
                         className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
                </div>
              </div>
              <button type="submit" data-testid="shift-save"
                      className="w-full h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">Save</button>
            </form>
          </div>
        </div>
      )}
    </AppShell>
  );
}
